import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
	OrderProduct,
	Shopper,
	SetProduct,
	PartialOrder,
	DinCollectionMethod,
	ProductDinValues,
} from 'common/types';
import { updateActivePartialOrder } from 'actions';
import { ReduxState } from 'services/types';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import TextField from '@mui/material/TextField';
import { getNewDinValues } from './utils';
import { useTranslation } from 'services/localization';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';

interface OwnProps {
	product: OrderProduct | SetProduct;
	shopper: Shopper;
	productId: string;
	setIndex: null | number;
	name: string;
	value: number | string;
	type: string;
	disabled: boolean;
	helperText?: string;
	onBlur?: (value: string | number) => void;
	error?: boolean;
	label?: string;
	dinCollectionMethod: DinCollectionMethod;
}

interface StateToProps {
	products: OrderProduct[];
}

interface DispatchProps {
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
}

type InputEventType = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

type Props = StateToProps & DispatchProps & OwnProps & WithStyles<typeof styles>;

export const ProductDinOptionsInput = (props: Props) => {
	const {
		classes,
		product,
		products,
		productId,
		setIndex,
		error,
		shopper,
		dinCollectionMethod,
	} = props;
	const [value, setValue] = useState<string | number>(props.value);
	const { t } = useTranslation();

	useEffect(() => {
		setValue(props.value);
	}, [props.value]);

	const handleInputChange = (e: InputEventType) => {
		setValue(e.target.value);
	};

	const handleBlur = (e: InputEventType) => {
		let savedValue: string | number | null = value && !isNaN(Number(value)) ? Number(value) : value;
		const name = e.target.name;
		if (name === 'customDin' && savedValue === '') {
			savedValue = null;
			setValue(props.value);
		}
		handleValueSave({
			name,
			value: savedValue,
		});
	};

	const handleValueSave = ({ name, value }: { name: string; value: number | string | null }) => {
		const updatedDinValues: ProductDinValues = {
			...(product.dinValues || {}),
			[name]: value,
		};
		const productToUpdate = products.find((p) => p.id === productId);
		if (!productToUpdate) {
			return;
		}
		let updatedPartialProduct: Partial<OrderProduct | SetProduct> = {
			dinValues: getNewDinValues(
				{ ...product, dinValues: updatedDinValues },
				{
					userProperties: shopper.userProperties,
					t,
					dinCollectionMethod,
				},
			),
		};
		if (setIndex != null) {
			const setProducts = productToUpdate.setProducts.map((setProduct, index) => {
				if (index === setIndex) {
					return {
						...setProduct,
						...updatedPartialProduct,
					};
				}
				return setProduct;
			});
			updatedPartialProduct = { setProducts };
		}
		props.updateActivePartialOrder({
			products: [
				{
					id: productToUpdate.id,
					data: updatedPartialProduct,
				},
			],
		});
	};

	const showInputError = error && !value && value !== 0;

	return (
		<TextField
			name={props.name}
			label={props.label || props.name}
			margin="none"
			variant="outlined"
			type={props.type}
			value={value}
			onChange={handleInputChange}
			onBlur={handleBlur}
			error={showInputError}
			disabled={props.disabled}
			classes={{
				root: classes.formRoot,
			}}
			InputProps={{
				classes: {
					error: classes.cssOutlinedInputError,
					input: classes.input,
					root: classes.cssOutlinedInput,
					notchedOutline: showInputError
						? classes.notchedOutlineError
						: props.disabled
						? classes.notchedOutlineDisabled
						: classes.notchedOutline,
				},
			}}
			FormHelperTextProps={{
				classes: {
					contained: classes.helperLabel,
				},
			}}
			InputLabelProps={{
				variant: 'outlined',
				classes: {
					error: classes.error,
					focused: classes.focused,
				},
				shrink: true,
			}}
			helperText={props.helperText}
		/>
	);
};

const mapStateToProps = (state: ReduxState): StateToProps => {
	const products = RentalViewSelectors.orderProducts(state);
	return {
		products,
	};
};

const styles = (theme: Theme) =>
	createStyles({
		formRoot: {
			marginTop: '0px !important',
			width: '100%',
		},
		helperLabel: {
			margin: 0,
			marginTop: '4px',
		},
		input: {
			padding: '4px 6px',
			fontSize: '1.4rem !important',
			lineHeight: 'normal',
			height: 32,
		},
		focused: {},
		error: {},
		cssOutlinedInput: {
			'&$notchedOutline': {
				borderColor: `#3BD691 !important`,
			},
		},
		cssOutlinedInputError: {
			'&$notchedOutline': {
				borderColor: `${theme.palette.error.main} !important`,
			},
		},
		notchedOutline: {
			borderColor: '#3BD691 !important',
		},
		notchedOutlineDisabled: {
			borderColor: theme.palette.border.inputOutline,
		},
		notchedOutlineError: {
			borderColor: `${theme.palette.error.main} !important`,
		},
		withoutMargin: {
			marginTop: '0px',
		},
		extraMargin: {
			marginTop: '8px',
			maxWidth: '50%',
		},
	});

const dispatchProps: DispatchProps = {
	updateActivePartialOrder,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(ProductDinOptionsInput));
